import React from 'react';
import PropTypes from 'prop-types';
import getCaretPosition from '../../utilities/get-input-element-caret-position';
import replaceFullWidthChars from '../../utilities/replace-full-width-characters';
import hasTextSelected from '../../utilities/has-text-selected';
import bubbleUpEvents from '../../utilities/bubble-up-events';
import unpick from '../../../utilities/unpick';
import { isIE } from '../../utilities/payform';
import HiddenComponents from './hidden-components';

function setValue(event, target, value) {
  event.preventDefault();
  setTimeout(() => {
    target.value = value; // eslint-disable-line no-param-reassign
  });
}

function formatCardExpiryString(expiryStr, yearLength, separator) {
  const expiry = replaceFullWidthChars(expiryStr);
  const parts = expiry.match(/^\D*(\d{1,2})(\D+)?(\d{1,4})?/);
  if (parts === null) {
    return '';
  }
  let mon = parts[1] || '';
  let sep = parts[2] || '';
  let year = parts[3] || '';

  // control browser autofill
  if (String(yearLength) === '2') {
    year = year.slice(-2);
  }

  const separatorWhenDeleting = separator.slice(0, -1);

  if (year.length > 0) {
    sep = separator;
    // user presses backspace and is about to delete the last digit in the month
  } else if (sep === separatorWhenDeleting) {
    mon = mon.substring(0, 1);
    sep = '';
  } else if (mon.length === 2 || sep.length > 0) {
    sep = separator;
  } else if (mon.length === 1 && mon !== '0' && mon !== '1') {
    mon = `0${mon}`;
    sep = separator;
  }
  return `${mon}${sep}${year}`;
}

function formatBackExpiry(e) {
  if (e.which !== 8) {
    return;
  }

  const { target } = e;
  const { value } = target;
  const cursor = getCaretPosition(target);

  if (cursor && cursor !== value.length) {
    return;
  }
  if (/\d\s\/\s$/.test(value)) {
    setValue(e, target, value.replace(/\d\s\/\s$/, ''));
  }
}

function restrictNumeric(e) {
  if (e.metaKey || e.ctrlKey) {
    return;
  }
  if (e.which === 0) {
    return;
  }
  if (e.which < 33) {
    return;
  }
  const input = String.fromCharCode(e.which);
  if (!/^\d+$/.test(input)) {
    e.preventDefault();
  }
}

function restrictExpiry(e, yearLength = 4) {
  const digit = String.fromCharCode(e.which);
  if (!/^\d+$/.test(digit)) {
    return;
  }
  if (hasTextSelected(e.target)) {
    return;
  }
  let value = e.target.value + digit;
  value = value.replace(/\D/g, '');
  if (value.length > Number(yearLength) + 2) {
    e.preventDefault();
  }
}

function formatCardExpiry(e, separator) {
  const digit = String.fromCharCode(e.which);
  if (!/^\d+$/.test(digit)) {
    return;
  }
  const { target } = e;
  const val = `${target.value}${digit}`;

  if (/^\d$/.test(val) && val !== '0' && val !== '1') {
    setValue(e, target, `0${val}${separator}`);
  } else if (/^\d\d$/.test(val)) {
    setValue(e, target, `${val}${separator}`);
  }
}

function formatForwardSlashAndSpace(e, separator) {
  const { target } = e;
  const which = String.fromCharCode(e.which);
  const isSeparatorChar = which === '/' || which === ' ';
  if (!isSeparatorChar) {
    return;
  }
  const val = target.value;
  if (/^\d$/.test(val) && val !== '0') {
    target.value = `0${val}${separator}`;
  }
}

function formatForwardExpiry(e, separator) {
  const { target } = e;
  const digit = String.fromCharCode(e.which);
  if (!/^\d+$/.test(digit)) {
    return;
  }
  const val = target.value;
  if (/^\d\d$/.test(val)) {
    target.value = `${val}${separator}`;
  }
}

function reFormatExpiry(e, yearLength, separator) {
  let cursor;
  if (e.target.value === '') {
    return;
  }
  e.target.value = formatCardExpiryString(e.target.value, yearLength, separator);
  cursor = getCaretPosition(e.target);
  if (cursor != null && e.type !== 'change') {
    return e.target.setSelectionRange(cursor, cursor);
  }
}

export default function CardExpirationDate(props) {
  const { onChange, onKeyDown, onKeyPress, isValid, hiddenField, yearLength, separator } = props;
  const p = unpick(props, ['ariaLabel', 'isValid', 'hiddenField', 'yearLength']);

  const eventProps = {
    onChange(e) {
      if (!hiddenField) {
        bubbleUpEvents(e, [reFormatExpiry(e, yearLength, separator), onChange]);
      }
    },
    onInput(e) {
      if (!isIE()) {
        bubbleUpEvents(e, [reFormatExpiry(e, yearLength, separator), onChange]);
      }
    },
    onKeyDown(e) {
      bubbleUpEvents(e, [formatBackExpiry, onKeyDown]);
    },
    onKeyPress(e) {
      bubbleUpEvents(e, [
        restrictNumeric,
        restrictExpiry(e, yearLength),
        (e) => formatCardExpiry(e, separator),
        (e) => formatForwardSlashAndSpace(e, separator),
        (e) => formatForwardExpiry(e, separator),
        onKeyPress,
      ]);
    },
  };

  let hiddenComponents = null;

  if (!hiddenField) {
    hiddenComponents = {
      CreditCardNumber: {
        autoComplete: 'cc-number',
      },
      CardSecurityCode: {
        autoComplete: 'cc-csc',
      },
      CardHolderName: {
        autoComplete: 'cc-name',
      },
    };
  }

  return (
    <>
      <input
        {...p}
        {...eventProps}
        type="tel"
        aria-label={props.ariaLabel || 'Card expiration date'}
        aria-placeholder={props.placeholder || ''}
        aria-invalid={!isValid}
      />
      {!hiddenField && <HiddenComponents hiddenComponents={hiddenComponents} />}
    </>
  );
}

CardExpirationDate.displayName = 'CardExpirationDate';

CardExpirationDate.propTypes = {
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyPress: PropTypes.func,
  separator: PropTypes.string,
};

CardExpirationDate.defaultProps = {
  onChange: undefined,
  onKeyDown: undefined,
  onKeyPress: undefined,
  separator: ' / ',
};
